/**
 * Pipeline orchestrator — runs entity and single generation steps in sequence
 */

import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import { getManyToManyFields } from '@core-engine/schema/field-helpers/index.js'
import {
  loadSchema,
  normalizeEntitySchema,
  normalizeSchemaSlotLayout
} from '@core-engine/schema/schema-reader.js'
import { isSystemRelationshipTarget } from '@core-engine/schema/system-relationships.js'
import type { GeneratedFile, GeneratorOptions, Schema } from '@core-engine/schema/types.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace.js'
import { resolveExtensionNamespace, resolveProjectPaths } from '@next-adapter/config/paths.js'
import { generateActions, generateSingleActions } from '@next-adapter/generators/actions/index.js'
import { generateColumns } from '@next-adapter/generators/columns/index.js'
import { generateCreatePage } from '@next-adapter/generators/create-page.js'
import { generateDatabase } from '@next-adapter/generators/database.js'
import { generateEditPage } from '@next-adapter/generators/edit-page.js'
import { generateForm, generateSingleForm } from '@next-adapter/generators/form/index.js'
import { generateHook, generateSingleHook } from '@next-adapter/generators/hook.js'
import { namespaceGeneratedFiles } from '@next-adapter/generators/namespace-output.js'
import { updateNavigation } from '@next-adapter/generators/navigation.js'
import { generatePage } from '@next-adapter/generators/page.js'
import { generatePageContent } from '@next-adapter/generators/page-content.js'
import { generateSinglePage } from '@next-adapter/generators/single-page.js'
import { generateTable } from '@next-adapter/generators/table.js'

export interface PipelineResult {
  success: boolean
  files: GeneratedFile[]
  errors: string[]
}

function loadRelationshipSchemas(
  schema: Schema,
  cwd: string,
  schemasDir: string
): Map<string, Schema> {
  const targets = new Set(
    getManyToManyFields(schema.fields)
      .map((field) => field.relationship)
      .filter((relationship): relationship is string => Boolean(relationship))
  )

  const relatedSchemas = new Map<string, Schema>()
  for (const relationship of targets) {
    if (isSystemRelationshipTarget(relationship)) {
      continue
    }

    const loaded = loadSchema(`${cwd}/${schemasDir}`.replace(/\/+/g, '/'), relationship)
    if (loaded.type === 'form') {
      throw new Error(
        `Relationship "${schema.name}" -> "${relationship}" cannot target a form schema.`
      )
    }
    relatedSchemas.set(relationship, loaded.schema)
  }

  return relatedSchemas
}

/**
 * Run the full entity generation pipeline.
 */
export function runEntityPipeline(
  schema: Schema,
  cwd: string,
  config: ResolvedBetterstartConfig,
  options: GeneratorOptions = {}
): PipelineResult {
  const normalizedSchema = normalizeEntitySchema(schema)
  const paths = resolveProjectPaths(config)
  const outputNamespace = resolveExtensionNamespace(normalizedSchema.name, options.outputNamespace)
  const adminNamespace = resolveAdminNamespace(config.frameworkConfig.next.namespace)
  const namespacedOptions = {
    ...options,
    outputNamespace,
    adminDir: paths.adminDir,
    adminRoutePath: adminNamespace.routePath,
    adminImportAlias: adminNamespace.alias
  }
  const files: GeneratedFile[] = []
  const errors: string[] = []
  const relatedSchemas = loadRelationshipSchemas(normalizedSchema, cwd, paths.schemasDir)

  const steps: { name: string; run: () => GeneratedFile[] }[] = [
    {
      name: 'Database schema',
      run: () =>
        generateDatabase(normalizedSchema, paths.adminDbDir, namespacedOptions, relatedSchemas)
          .files
    },
    {
      name: 'Server actions',
      run: () =>
        generateActions(normalizedSchema, paths.adminActionsDir, {
          force: options.force,
          nextMajorVersion: config.nextMajorVersion,
          outputNamespace
        }).files
    },
    {
      name: 'React Query hook',
      run: () => generateHook(normalizedSchema, paths.adminHooksDir, namespacedOptions).files
    },
    {
      name: 'Column definitions',
      run: () => generateColumns(normalizedSchema, paths.pagesDir, namespacedOptions).files
    },
    {
      name: 'Table component',
      run: () => generateTable(normalizedSchema, paths.pagesDir, namespacedOptions).files
    },
    {
      name: 'Page content',
      run: () => generatePageContent(normalizedSchema, paths.pagesDir, namespacedOptions).files
    },
    {
      name: 'Page (server)',
      run: () => generatePage(normalizedSchema, paths.pagesDir, namespacedOptions).files
    }
  ]

  // Steps 8-10 are conditional on schema.actions
  if (normalizedSchema.actions?.create || normalizedSchema.actions?.edit) {
    steps.push({
      name: 'Form',
      run: () => generateForm(normalizedSchema, paths.pagesDir, namespacedOptions).files
    })
  }

  if (normalizedSchema.actions?.create) {
    steps.push({
      name: 'Create page',
      run: () => generateCreatePage(normalizedSchema, paths.pagesDir, namespacedOptions).files
    })
  }

  if (normalizedSchema.actions?.edit) {
    steps.push({
      name: 'Edit page',
      run: () => generateEditPage(normalizedSchema, paths.pagesDir, namespacedOptions).files
    })
  }

  steps.push({
    name: 'Navigation',
    run: () => updateNavigation(normalizedSchema, paths.adminNavigationDir, namespacedOptions).files
  })

  // Execute each step
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    const stepNum = i + 1
    try {
      const result = step.run()
      files.push(...result)
      if (!options.silent) console.log(`   ${stepNum}. ${step.name} ✓`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      errors.push(`${step.name}: ${msg}`)
      if (!options.silent) console.error(`   ${stepNum}. ${step.name} ✗ — ${msg}`)
    }
  }

  return {
    success: errors.length === 0,
    files: namespaceGeneratedFiles(files, config.frameworkConfig.next.namespace),
    errors
  }
}

/**
 * Run the single (singleton) generation pipeline.
 */
export function runSinglePipeline(
  schema: Schema,
  cwd: string,
  config: ResolvedBetterstartConfig,
  options: GeneratorOptions = {}
): PipelineResult {
  const normalizedSchema = normalizeSchemaSlotLayout(schema)
  const paths = resolveProjectPaths(config)
  const outputNamespace = resolveExtensionNamespace(normalizedSchema.name, options.outputNamespace)
  const adminNamespace = resolveAdminNamespace(config.frameworkConfig.next.namespace)
  const namespacedOptions = {
    ...options,
    outputNamespace,
    adminDir: paths.adminDir,
    adminRoutePath: adminNamespace.routePath,
    adminImportAlias: adminNamespace.alias
  }
  const files: GeneratedFile[] = []
  const errors: string[] = []
  const relatedSchemas = loadRelationshipSchemas(normalizedSchema, cwd, paths.schemasDir)

  const steps: { name: string; run: () => GeneratedFile[] }[] = [
    {
      name: 'Database schema',
      run: () =>
        generateDatabase(normalizedSchema, paths.adminDbDir, namespacedOptions, relatedSchemas)
          .files
    },
    {
      name: 'Server actions',
      run: () =>
        generateSingleActions(normalizedSchema, paths.adminActionsDir, {
          force: options.force,
          nextMajorVersion: config.nextMajorVersion,
          outputNamespace
        }).files
    },
    {
      name: 'React Query hook',
      run: () => generateSingleHook(normalizedSchema, paths.adminHooksDir, namespacedOptions).files
    },
    {
      name: 'Form',
      run: () => generateSingleForm(normalizedSchema, paths.pagesDir, namespacedOptions).files
    },
    {
      name: 'Single page',
      run: () => generateSinglePage(normalizedSchema, paths.pagesDir, namespacedOptions).files
    },
    {
      name: 'Navigation',
      run: () =>
        updateNavigation(normalizedSchema, paths.adminNavigationDir, namespacedOptions).files
    }
  ]

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    const stepNum = i + 1
    try {
      const result = step.run()
      files.push(...result)
      if (!options.silent) console.log(`   ${stepNum}. ${step.name} ✓`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      errors.push(`${step.name}: ${msg}`)
      if (!options.silent) console.error(`   ${stepNum}. ${step.name} ✗ — ${msg}`)
    }
  }

  return {
    success: errors.length === 0,
    files: namespaceGeneratedFiles(files, config.frameworkConfig.next.namespace),
    errors
  }
}

// Re-export individual generators for direct use
export { generateActions, generateSingleActions } from '@next-adapter/generators/actions/index.js'
export { generateColumns } from '@next-adapter/generators/columns/index.js'
export { generateCreatePage } from '@next-adapter/generators/create-page.js'
export { generateDatabase } from '@next-adapter/generators/database.js'
export { generateEditPage } from '@next-adapter/generators/edit-page.js'
export { generateForm, generateSingleForm } from '@next-adapter/generators/form/index.js'
export { generateHook, generateSingleHook } from '@next-adapter/generators/hook.js'
export { updateNavigation } from '@next-adapter/generators/navigation.js'
export { generatePage } from '@next-adapter/generators/page.js'
export { generatePageContent } from '@next-adapter/generators/page-content.js'
export { generateSinglePage } from '@next-adapter/generators/single-page.js'
export { generateTable } from '@next-adapter/generators/table.js'
